import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Briefcase,
  Calendar,
  Users,
  User,
  Plus,
  LogOut,
  Settings as SettingsIcon,
  Package,
  Truck,
} from 'lucide-react';
import { useJobStore } from '../lib/store';
import { useAuth } from '../lib/auth';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: any;
  action: () => void;
  group: string;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { logout, isAdmin } = useAuth();
  const jobs = useJobStore((s) => s.jobs);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQ('');
      setCursor(0);
    }
  }, [open]);

  const items: CommandItem[] = useMemo(() => {
    const nav: CommandItem[] = [
      { id: 'nav-dash', label: 'Dashboard', icon: LayoutDashboard, action: () => navigate('/'), group: 'Navigate' },
      { id: 'nav-jobs', label: 'Jobs', icon: Briefcase, action: () => navigate('/jobs'), group: 'Navigate' },
      { id: 'nav-cal', label: 'Calendar', icon: Calendar, action: () => navigate('/calendar'), group: 'Navigate' },
      { id: 'nav-cli', label: 'Clients', icon: Users, action: () => navigate('/clients'), group: 'Navigate' },
      { id: 'nav-cons', label: 'Consumables', icon: Package, action: () => navigate('/consumables'), group: 'Navigate' },
      { id: 'nav-parts', label: 'Parts Orders', icon: Truck, action: () => navigate('/parts-orders'), group: 'Navigate' },
      { id: 'nav-prof', label: 'Profile', icon: User, action: () => navigate('/profile'), group: 'Navigate' },
    ];
    if (isAdmin) {
      nav.push({
        id: 'nav-settings',
        label: 'Xero & Settings',
        icon: SettingsIcon,
        action: () => navigate('/settings'),
        group: 'Navigate',
      });
    }
    const actions: CommandItem[] = [];
    if (isAdmin) {
      actions.push({
        id: 'act-new-job',
        label: 'Book a new job',
        icon: Plus,
        action: () => navigate('/jobs/new'),
        group: 'Actions',
      });
    }
    actions.push({
      id: 'act-logout',
      label: 'Sign out',
      icon: LogOut,
      action: () => {
        logout();
        navigate('/login', { replace: true });
      },
      group: 'Actions',
    });
    const jobItems: CommandItem[] = jobs.slice(0, 20).map((j) => ({
      id: `job-${j.id}`,
      label: j.title,
      hint: `${j.job_number || ''} · ${j.client_name || ''} · ${j.status}`.trim(),
      icon: Briefcase,
      action: () => navigate(`/jobs/${j.id}`),
      group: 'Jobs',
    }));
    return [...nav, ...actions, ...jobItems];
  }, [jobs, isAdmin, navigate, logout]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(ql) ||
        (i.hint && i.hint.toLowerCase().includes(ql))
    );
  }, [items, q]);

  useEffect(() => {
    setCursor(0);
  }, [q]);

  const grouped = useMemo(() => {
    const g: Record<string, CommandItem[]> = {};
    filtered.forEach((i) => {
      if (!g[i.group]) g[i.group] = [];
      g[i.group].push(i);
    });
    return g;
  }, [filtered]);

  const flat = filtered;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[cursor];
      if (item) {
        item.action();
        setOpen(false);
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-pop border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 border-b border-slate-100 px-4">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search jobs, navigate, run actions…"
            className="flex-1 py-4 outline-none text-slate-900 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-flex text-[10px] font-mono bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {flat.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No results.</div>
          ) : (
            Object.entries(grouped).map(([group, list]) => (
              <div key={group}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {group}
                </div>
                {list.map((item) => {
                  const idx = flat.indexOf(item);
                  const isActive = idx === cursor;
                  return (
                    <button
                      key={item.id}
                      onMouseEnter={() => setCursor(idx)}
                      onClick={() => {
                        item.action();
                        setOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                        isActive ? 'bg-brand-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <item.icon
                        className={`h-4 w-4 shrink-0 ${
                          isActive ? 'text-brand-700' : 'text-slate-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium truncate ${
                            isActive ? 'text-brand-800' : 'text-slate-900'
                          }`}
                        >
                          {item.label}
                        </div>
                        {item.hint && (
                          <div className="text-xs text-slate-500 truncate">{item.hint}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-3 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-slate-100 rounded px-1 py-0.5">↑↓</kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-slate-100 rounded px-1 py-0.5">↵</kbd> select
          </span>
          <span className="ml-auto">Press ⌘K anywhere</span>
        </div>
      </div>
    </div>
  );
}
